import { FC, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import useBenguide from 'hooks/benguide';
import { useAppSelector } from 'hooks/redux';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import ContentPreviewer from 'components/ContentPreviewer/ContentPreviewer';
import Spinner from 'components/Spinner/Spinner';
import RemoveCustomSection from 'modules/benefitsWellness/components/RemoveCustomComponent/RemoveCustomSection';
import iconEdit from 'images/icon-edit.svg';
import iconDelete from 'images/icons/remove-icon.svg';
import AddYourText from 'modules/benefitsWellness/components/AddYourText/AddYourText';
import SectionMoveControls from 'modules/customPages/components/SectionMoveControls/SectionMoveControls';
import ContentCardEditor from './ContentCardEditor';
import styles from './contentCard.module.less';

interface ContentCardProps {
  id: string;
  title?: string;
  content?: string;
  customPageId: string;
  onSave?: (id: string, title: string, content: string) => void;
  onDelete?: (id: string) => void;
  moveProps?: {
    onMoveUp: () => void;
    onMoveDown: () => void;
    hideUp: boolean;
    hideDown: boolean;
    showControls: boolean;
  };
}

const ContentCard: FC<ContentCardProps> = ({
  id,
  title = '',
  content = '',
  customPageId,
  onSave,
  onDelete,
  moveProps,
}) => {
  const { masterId, isEditMode } = useBenguide();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const richMediaContent = useAppSelector(
    (state) => state.customPages.richMediaContent
  );
  const key = `${customPageId}-${id}`;
  const reduxData = richMediaContent[key];

  const decodeHtml = (str: string) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  };

  const displayTitle =
    reduxData?.title !== undefined
      ? decodeHtml(reduxData.title)
      : decodeHtml(title);
  const displayContent =
    reduxData?.content !== undefined ? reduxData.content : content;
  const showAddContentBtn =
    isEmpty(displayTitle) && isEmpty(displayContent) && isEditMode;

  const handleSave = useCallback(
    (newTitle: string, newContent: string) => {
      if (onSave) {
        setIsLoading(true);
        onSave(id, newTitle, newContent);
        setIsLoading(false);
      }
    },
    [id, onSave]
  );

  const handleDelete = useCallback(() => {
    setShowDeleteConfirmation(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (onDelete) {
      onDelete(id);
    }
    setShowDeleteConfirmation(false);
  }, [id, onDelete]);

  return (
    <div
      className={`${styles.contentCardWrapper} ${
        !isEditMode || !isEmpty(displayTitle) ? styles.previewMode : ''
      }`}
    >
      {moveProps && (
        <SectionMoveControls
          onMoveUp={moveProps.onMoveUp}
          onMoveDown={moveProps.onMoveDown}
          hideUp={moveProps.hideUp}
          hideDown={moveProps.hideDown}
          showControls={moveProps.showControls}
        />
      )}
      <div className={styles.twoColumnLayout}>
        {/* Left Column - Title Section */}
        <div className={styles.titleColumn}>
          <div
            className={`${styles.titleLabel} ${
              displayTitle ? styles.withBorder : ''
            }`}
          >
            {displayTitle ? displayTitle : 'Title'}
          </div>
          <div className={styles.titlePreview}>
            {displayTitle ? (
              <> </>
            ) : (
              <div className={styles.titlePlaceholder}>
                <div className={styles.placeholderBar}></div>
                <div className={styles.placeholderBar}></div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Content Section */}
        <div className={styles.contentColumn}>
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              {showAddContentBtn || !displayContent ? (
                <EditableHotspot
                  editText="Edit"
                  alwaysVisible={false}
                  editIcon={<img src={iconEdit} alt="edit" />}
                  deleteText="Remove"
                  deleteIcon={<img src={iconDelete} alt="delete" />}
                  onDelete={handleDelete}
                  showDeleteButton={isEditMode}
                  customModal={(
                    visible: boolean,
                    onClose: () => void,
                    editorKey: string
                  ) => (
                    <ContentCardEditor
                      isOpenModal={visible}
                      closeModal={onClose}
                      title={displayTitle}
                      content={displayContent}
                      benguideId={masterId}
                      onSave={handleSave}
                      editorKey={editorKey}
                      customPageId={customPageId}
                      customPageSectionId={id}
                    />
                  )}
                >
                  <div>
                    <div
                      onClick={() => setIsModalOpen(true)}
                      className={styles.addContentButton}
                    >
                      <AddYourText />
                    </div>
                    <div className={styles.contentPlaceholder}>
                      <div className={styles.placeholderBar}></div>
                      <div className={styles.placeholderBar}></div>
                      <div className={styles.placeholderBar}></div>
                      <div className={styles.placeholderBar}></div>
                    </div>
                  </div>
                </EditableHotspot>
              ) : (
                <EditableHotspot
                  editText="Edit"
                  alwaysVisible={showAddContentBtn}
                  editIcon={<img src={iconEdit} alt="edit" />}
                  deleteText="Remove"
                  deleteIcon={<img src={iconDelete} alt="delete" />}
                  onDelete={handleDelete}
                  showDeleteButton={!isEmpty(displayContent) && isEditMode}
                  customModal={(
                    visible: boolean,
                    onClose: () => void,
                    editorKey: string
                  ) => (
                    <ContentCardEditor
                      isOpenModal={visible}
                      closeModal={onClose}
                      title={displayTitle}
                      content={displayContent}
                      benguideId={masterId}
                      onSave={handleSave}
                      editorKey={editorKey}
                      customPageId={customPageId}
                      customPageSectionId={id}
                    />
                  )}
                >
                  <div className={styles.contentPreview}>
                    {displayContent ? (
                      <ContentPreviewer htmlContent={displayContent} />
                    ) : (
                      <div className={styles.contentPlaceholder}>
                        <div className={styles.placeholderBar}></div>
                        <div className={styles.placeholderBar}></div>
                        <div className={styles.placeholderBar}></div>
                        <div className={styles.placeholderBar}></div>
                      </div>
                    )}
                  </div>
                </EditableHotspot>
              )}

              {/* Modal for AddYourText click */}
              {showAddContentBtn && (
                <ContentCardEditor
                  isOpenModal={isModalOpen}
                  closeModal={() => setIsModalOpen(false)}
                  title={displayTitle}
                  content={displayContent}
                  benguideId={masterId}
                  onSave={(newTitle: string, newContent: string) => {
                    handleSave(newTitle, newContent);
                    setIsModalOpen(false);
                  }}
                  editorKey={`add-content-${id}`}
                  customPageId={customPageId}
                  customPageSectionId={id}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <RemoveCustomSection
        visible={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onRemove={confirmDelete}
      />
    </div>
  );
};

ContentCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  content: PropTypes.string,
  customPageId: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  moveProps: PropTypes.shape({
    onMoveUp: PropTypes.func.isRequired,
    onMoveDown: PropTypes.func.isRequired,
    hideUp: PropTypes.bool.isRequired,
    hideDown: PropTypes.bool.isRequired,
    showControls: PropTypes.bool.isRequired,
  }),
};

ContentCard.defaultProps = {
  title: '',
  content: '',
  onSave: undefined,
};

export default ContentCard;
